/**
 * Report Export Hook
 * Custom hook for handling report exports with progress tracking
 */

import { useState, useCallback } from 'react';
import ExportService from '../services/ExportService';
import { REPORT_FORMATS } from '../../reports.generators/types/reportTypes';

export const useReportExport = () => {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [lastExport, setLastExport] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);

  const exportReport = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    try {
      setExporting(true);
      setExportError(null);
      setExportProgress(0);

      // Validate export request
      const validation = ExportService.validateExportRequest(
        content, 
        reportType, 
        options.format
      );
      
      if (!validation.isValid) {
        throw new Error(`Export validation failed: ${validation.errors.join(', ')}`);
      }

      setExportProgress(25);

      // Perform export
      const result = await ExportService.exportReport(content, reportType, parameters, options);
      
      setExportProgress(75);
      setLastExport(result);
      setExportProgress(100);

      return result;
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error.message);
      return null;
    } finally {
      setExporting(false);
      setTimeout(() => setExportProgress(0), 2000); // Reset progress after 2s
    }
  }, []);

  const exportToPDF = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    return await exportReport(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.PDF
    });
  }, [exportReport]);

  const exportToCSV = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    return await exportReport(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.CSV
    });
  }, [exportReport]);

  const exportToExcel = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    return await exportReport(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.EXCEL
    });
  }, [exportReport]);

  const exportToHTML = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    return await exportReport(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.HTML
    });
  }, [exportReport]);

  const exportToJSON = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    return await exportReport(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.JSON
    });
  }, [exportReport]);

  const printReport = useCallback(async (content, reportType, options = {}) => {
    try {
      setExporting(true);
      setExportError(null);

      const result = await ExportService.printReport(content, reportType, options);
      setLastExport({ ...result, type: 'print' });
      
      return result;
    } catch (error) {
      console.error('Print failed:', error);
      setExportError(error.message);
      return null;
    } finally {
      setExporting(false);
    }
  }, []);

  const batchExport = useCallback(async (exportRequests) => {
    try {
      setExporting(true);
      setExportError(null);
      setExportProgress(0);

      const results = await ExportService.batchExport(exportRequests);
      
      setExportProgress(100);
      setLastExport({ type: 'batch', results });

      return results;
    } catch (error) {
      console.error('Batch export failed:', error);
      setExportError(error.message);
      return [];
    } finally {
      setExporting(false);
      setTimeout(() => setExportProgress(0), 2000);
    }
  }, []);

  const getExportHistory = useCallback((limit = 10) => {
    return ExportService.getExportHistory(limit);
  }, []);

  const clearError = useCallback(() => {
    setExportError(null);
  }, []);

  const clearLastExport = useCallback(() => {
    setLastExport(null);
  }, []);

  const getSupportedFormats = useCallback((reportType) => {
    return ExportService.getSupportedFormats(reportType);
  }, []);

  return {
    // State
    exporting,
    exportError,
    lastExport,
    exportProgress,

    // Actions
    exportReport,
    exportToPDF,
    exportToCSV,
    exportToExcel,
    exportToHTML,
    exportToJSON,
    printReport,
    batchExport,

    // Utilities
    getExportHistory,
    getSupportedFormats,
    clearError,
    clearLastExport,

    // Status helpers
    canExport: !exporting,
    hasError: !!exportError,
    isComplete: exportProgress === 100 && !exporting
  };
};

// Specialized hook for print functionality
export const usePrintReport = () => {
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState(null);

  const print = useCallback(async (content, reportType, options = {}) => {
    try {
      setPrinting(true);
      setPrintError(null);

      const result = await ExportService.printReport(content, reportType, options);
      return result;
    } catch (error) {
      console.error('Print failed:', error);
      setPrintError(error.message);
      return null;
    } finally {
      setPrinting(false);
    }
  }, []);

  const printWithPreview = useCallback(async (content, reportType, options = {}) => {
    try {
      // Create print preview in new window
      const printHTML = ExportService.preparePrintHTML(content, options);
      const previewWindow = ExportService.openHTMLInNewWindow(printHTML);
      
      return { success: true, previewWindow };
    } catch (error) {
      console.error('Print preview failed:', error);
      setPrintError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  const clearPrintError = useCallback(() => {
    setPrintError(null);
  }, []);

  return {
    printing,
    printError,
    print,
    printWithPreview,
    clearPrintError,
    canPrint: !printing
  };
};

// Hook for download functionality
export const useReportDownload = () => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadHistory, setDownloadHistory] = useState([]);

  const download = useCallback(async (content, reportType, parameters = {}, options = {}) => {
    try {
      setDownloading(true);
      setDownloadError(null);

      const exportOptions = {
        ...options,
        download: true,
        openInNewWindow: false
      };

      const result = await ExportService.exportReport(
        content, 
        reportType, 
        parameters, 
        exportOptions
      );

      // Add to download history
      setDownloadHistory(prev => [{
        filename: result.filename,
        format: options.format || REPORT_FORMATS.HTML,
        timestamp: new Date(),
        reportType
      }, ...prev.slice(0, 9)]);

      return result;
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(error.message);
      return null;
    } finally {
      setDownloading(false);
    }
  }, []);

  const downloadPDF = useCallback((content, reportType, parameters = {}, options = {}) => {
    return download(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.PDF
    });
  }, [download]);

  const downloadCSV = useCallback((content, reportType, parameters = {}, options = {}) => {
    return download(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.CSV
    });
  }, [download]);

  const downloadExcel = useCallback((content, reportType, parameters = {}, options = {}) => {
    return download(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.EXCEL
    });
  }, [download]);

  const downloadHTML = useCallback((content, reportType, parameters = {}, options = {}) => {
    return download(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.HTML
    });
  }, [download]);

  const downloadJSON = useCallback((content, reportType, parameters = {}, options = {}) => {
    return download(content, reportType, parameters, {
      ...options,
      format: REPORT_FORMATS.JSON
    });
  }, [download]);

  const clearDownloadError = useCallback(() => {
    setDownloadError(null);
  }, []);

  const clearDownloadHistory = useCallback(() => {
    setDownloadHistory([]);
  }, []);

  return {
    downloading,
    downloadError,
    downloadHistory,
    download,
    downloadPDF,
    downloadCSV,
    downloadExcel,
    downloadHTML,
    downloadJSON,
    clearDownloadError,
    clearDownloadHistory,
    canDownload: !downloading
  };
};